import {
  Button,
  Tooltip,
  useComputedColorScheme,
  useMantineTheme,
  Modal,
  Loader,
  Image,
} from '@mantine/core';

import { IconDownload, IconEye } from '@tabler/icons-react';
import { generateGetSignedUrl } from 'api/action/s3';
import { useDownloadFromS3 } from 'hooks';
import { useState } from 'react';

import { buttonTextColor } from 'utils/buttonUtils';

import * as Sentry from '@sentry/react';

interface DownloadButtonProps {
  filename: string;
  bucket: string;
  ToolTipLabel?: string;
  openInContext?: boolean; // New prop
  disabled?: boolean;
}

export const DownloadButton: React.FC<DownloadButtonProps> = ({
  bucket,
  filename,
  ToolTipLabel = 'Download',
  openInContext = false, // Default to false
  disabled = false,
}) => {
  const theme = useMantineTheme();
  const computedColorScheme = useComputedColorScheme('dark');
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  const downloadFile = useDownloadFromS3(bucket, filename, setLoading);
  const openInContextModal = async () => {
    try {
      setLoading(true);
      if (filename) {
        const url = await generateGetSignedUrl({
          bucket: bucket,
          key: filename,
        });
        if (openInContext) {
          setFileUrl(url);
          setModalOpen(true);
        }
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip label={ToolTipLabel} position="top" withArrow>
        <Button
          onClick={openInContext ? openInContextModal : downloadFile}
          variant="outline"
          color={theme.colors[`primary-${computedColorScheme}`][0]}
          c={buttonTextColor(computedColorScheme, false)}
          loading={loading}
          disabled={disabled}
          size="md"
        >
          {openInContext ? <IconEye size={20} /> : <IconDownload size={20} />}
        </Button>
      </Tooltip>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Day Sheet"
        size="xl"
        centered
        closeOnEscape
        closeOnClickOutside
      >
        {fileUrl ? (
          <div
            style={{
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
            }}
          >
            <Image
              src={fileUrl}
              radius="md"
              title="Preview Content"
              h={700}
              w="auto"
              fit="contain"
              style={{ align: 'none' }}
            />
          </div>
        ) : (
          <Loader />
        )}
      </Modal>
    </>
  );
};
